/**
 * ApiKeyPrompt — shown above the chat composer when the user tries to send a message but has not saved
 * an API key for any model yet. It explains what is needed in three steps and takes them to the Models page.
 * Presentational only: the chat decides when to show it (RunDrawer) and what the buttons do.
 */
import React from "react";
import { KeyRound, ArrowRight, X, ShieldCheck } from "lucide-react";

const STEPS = [
  "Open Models and add a model",
  "Paste the API key from its provider",
  "Come back and send your message",
];

export default function ApiKeyPrompt({ onGoToModels, onDismiss }) {
  return (
    <section
      role="region"
      aria-label="API key required"
      style={{
        position: "relative",
        marginBottom: 10,
        padding: "16px 18px 14px",
        borderRadius: 14,
        border: "1px solid var(--app-primary-soft-2)",
        background: "linear-gradient(135deg, var(--app-primary-soft), var(--lyra-color-surface) 70%)",
        color: "var(--lyra-color-text)",
        fontFamily: "inherit",
      }}
    >
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss"
        style={{ position: "absolute", top: 10, right: 10, display: "flex", padding: 4, border: "none", background: "none", cursor: "pointer", color: "var(--lyra-color-text-muted)" }}
      >
        <X size={16} />
      </button>

      <div style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
        <div style={{ flexShrink: 0, width: 42, height: 42, borderRadius: 12, display: "grid", placeItems: "center", background: "var(--lyra-color-primary)", color: "var(--lyra-color-primary-fg, #fff)" }}>
          <KeyRound size={20} />
        </div>
        <div style={{ minWidth: 0, flex: 1, paddingRight: 18 }}>
          <div style={{ fontSize: 15, fontWeight: 700, letterSpacing: "-0.01em" }}>Add an API key to start chatting</div>
          <p style={{ margin: "4px 0 0", fontSize: 13, lineHeight: 1.5, color: "var(--lyra-color-text-muted)" }}>
            Lyra answers with a model from your own provider account. Add a key for any model you like, such as OpenAI, Anthropic, Google Gemini or Groq.
          </p>

          <ol style={{ listStyle: "none", margin: "12px 0 0", padding: 0, display: "flex", flexWrap: "wrap", gap: "8px 18px" }}>
            {STEPS.map((s, i) => (
              <li key={s} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12.5, color: "var(--app-text-2)" }}>
                <span style={{ width: 20, height: 20, borderRadius: "50%", display: "grid", placeItems: "center", fontSize: 11, fontWeight: 700, background: "var(--app-primary-soft-2)", color: "var(--app-primary-deep)" }}>{i + 1}</span>
                {s}
              </li>
            ))}
          </ol>

          <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 10, marginTop: 14 }}>
            <button
              type="button"
              onClick={onGoToModels}
              style={{ display: "inline-flex", alignItems: "center", gap: 7, height: 36, padding: "0 16px", border: "none", borderRadius: 10, background: "var(--lyra-color-primary)", color: "var(--lyra-color-primary-fg, #fff)", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}
            >
              Go to Models <ArrowRight size={15} />
            </button>
            <button
              type="button"
              onClick={onDismiss}
              style={{ height: 36, padding: "0 14px", border: "1px solid var(--lyra-color-border)", borderRadius: 10, background: "var(--lyra-color-surface)", color: "var(--lyra-color-text)", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}
            >
              Not now
            </button>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, color: "var(--lyra-color-text-muted)" }}>
              <ShieldCheck size={14} /> Keys are stored encrypted. Your message is kept.
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}
